import { useState, useMemo, useEffect } from 'react';
import { CardRecord } from '../../db/indexedDb';
import { TopicContent, TopicDictationResult, DictationPhase as Phase, TopicSentence } from './types';
import { scoreAllSentences } from './scorer';
import RecallPhase from './components/RecallPhase';
import PreviewPhase from './components/PreviewPhase';
import DictationPhase from './components/DictationPhase';
import ResultPhase from './components/ResultPhase';

interface Props {
  card: CardRecord;
  onEvaluated?: (result: TopicDictationResult) => void;
}

export default function TopicDictationCard({ card, onEvaluated }: Props) {
  const [phase, setPhase] = useState<Phase>('recall');
  const [recallChecks, setRecallChecks] = useState<Set<number>>(new Set());
  const [answers, setAnswers] = useState<Map<number, string>>(new Map());
  const [result, setResult] = useState<TopicDictationResult | null>(null);

  // 解析卡片内容
  const content = useMemo((): TopicContent => {
    try {
      const raw = card.content as Record<string, unknown>;
      const majorTopic = (raw?.majorTopic as string) || card.writingMeta?.topicTag || '';
      const subTopic = (raw?.subTopic as string) || '';
      const rawSentences = raw?.sentences as TopicSentence[] | undefined;

      if (!rawSentences?.length) {
        console.warn('[TopicDictation] Empty sentences for card:', card.id);
        return { majorTopic, subTopic, sentences: [] };
      }

      return { majorTopic, subTopic, sentences: rawSentences };
    } catch (err) {
      console.error('[TopicDictation] Failed to parse card content:', err);
      return { majorTopic: 'Error', subTopic: 'Error', sentences: [] };
    }
  }, [card]);

  // 卡片切换时重置状态
  useEffect(() => {
    setPhase('recall');
    setRecallChecks(new Set());
    setAnswers(new Map());
    setResult(null);
  }, [card.id]);

  const handleReveal = () => {
    setPhase('preview');
  };

  const handleStartDictation = (checks: Set<number>) => {
    setRecallChecks(checks);
    setPhase('dictation');
  };

  const handleSubmit = (finalAnswers: Map<number, string>) => {
    setAnswers(finalAnswers);
    const scored = scoreAllSentences(content.sentences, finalAnswers, recallChecks);
    setResult(scored);
    onEvaluated?.(scored);
    setPhase('result');
  };

  const handleBack = () => {
    setPhase('preview');
  };

  if (content.sentences.length === 0) {
    return (
      <div className="practice-card writing-compact topic-dictation">
        <div className="practice-card-header">
          <span className="practice-topic-tag">话题默写</span>
          <h3 className="practice-question">内容为空</h3>
        </div>
        <div className="topic-dictation-body">
          <p className="error-text">该卡片没有可用的句子内容。</p>
        </div>
      </div>
    );
  }

  return (
    <div className="practice-card writing-compact topic-dictation">
      {/* Header */}
      <div className="practice-card-header">
        <span className="practice-topic-tag">{content.majorTopic}</span>
        <h3 className="practice-question">{content.subTopic}</h3>
        <p className="topic-dictation-hint">
          {phase === 'recall' && '回忆这个话题的要点'}
          {phase === 'preview' && '勾选能想起的句子，然后开始默写'}
          {phase === 'dictation' && '根据中文提示默写英文'}
          {phase === 'result' && '查看默写结果'}
        </p>
      </div>

      {/* Phase Content */}
      <div className="topic-dictation-body">
        {phase === 'recall' && (
          <RecallPhase sentenceCount={content.sentences.length} onReveal={handleReveal} />
        )}
        {phase === 'preview' && (
          <PreviewPhase sentences={content.sentences} onStartDictation={handleStartDictation} />
        )}
        {phase === 'dictation' && (
          <DictationPhase
            sentences={content.sentences}
            initialAnswers={answers}
            onSubmit={handleSubmit}
            onBack={handleBack}
          />
        )}
        {phase === 'result' && result && <ResultPhase result={result} />}
      </div>
    </div>
  );
}
